import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, CheckCircle2, Target, Shield, Eye, Users } from 'lucide-react';
import { aboutContent, industries, certifications, clients } from '../data/mock';

const AboutPage = () => {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-4">
              About Grenew
            </p>
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-6">
              {aboutContent.headline}
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed">
              {aboutContent.paragraphs[0]}
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.pexels.com/photos/209251/pexels-photo-209251.jpeg"
                alt="Grenew Facility"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <div>
              {aboutContent.paragraphs.slice(1).map((para, i) => (
                <p key={i} className="text-gray-600 leading-relaxed mb-4">
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Facility */}
      <section className="py-16 lg:py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                {aboutContent.facility.headline}
              </p>
              <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-4">
                World-Class <span className="font-semibold">Infrastructure</span>
              </h2>
              <div className="flex items-center gap-2 text-gray-500 mb-6">
                <MapPin className="w-5 h-5 text-[#4fd1c5]" />
                <span>{aboutContent.facility.location}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {aboutContent.facility.stats.map((stat, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#4fd1c5]" />
                    <span className="text-gray-700">{stat}</span>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-white border border-gray-200">
                  <span className="text-3xl font-bold text-[#0a1628] block mb-1">50,000</span>
                  <span className="text-gray-500 text-sm">MT Annual Capacity</span>
                </div>
                <div className="p-6 bg-[#0a1628]">
                  <span className="text-3xl font-bold text-[#4fd1c5] block mb-1">GCC</span>
                  <span className="text-gray-400 text-sm">Regional Coverage</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.pexels.com/photos/1145434/pexels-photo-1145434.jpeg"
                alt="Facility"
                className="w-full aspect-video object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {aboutContent.focus.headline}
            </p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628]">
              Our <span className="font-semibold">Commitment</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Sustainability', desc: aboutContent.focus.items[0] },
              { icon: Shield, title: 'IP Protection', desc: aboutContent.focus.items[1] },
              { icon: Eye, title: 'Transparency', desc: aboutContent.focus.items[2] },
            ].map((item, i) => (
              <div key={i} className="p-8 bg-[#f8fafc] hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-gray-200">
                <item.icon className="w-10 h-10 text-[#4fd1c5] mb-5" />
                <h3 className="text-xl font-semibold text-[#0a1628] mb-3">{item.title}</h3>
                <p className="text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries & Clients */}
      <section className="py-16 lg:py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                Industries We Serve
              </p>
              <h3 className="text-2xl font-semibold text-[#0a1628] mb-6">Diverse Sectors</h3>
              <div className="flex flex-wrap gap-2">
                {industries.map((ind) => (
                  <span key={ind.name} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm">
                    {ind.name}
                  </span>
                ))}
              </div>
            </div>
            {/* <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                Our Clients
              </p>
              <h3 className="text-2xl font-semibold text-[#0a1628] mb-6">Fortune 500 & Beyond</h3>
              <div className="flex flex-wrap gap-2">
                {clients.map((client) => (
                  <span key={client} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm">
                    {client}
                  </span>
                ))}
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
            Ready to <span className="font-semibold">Partner?</span>
          </h2>
          <p className="text-gray-400 mb-8">
            Join leading enterprises in building a sustainable future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#4fd1c5] hover:bg-[#38b2ac] text-[#0a1628] px-8 py-4 font-semibold transition-colors">
              Request a Pickup
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/contact#partner" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white px-8 py-4 font-semibold transition-colors">
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
